// Core imports
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

// Component imports
import { Button, Card, Col, Empty, Row, Skeleton, Typography } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import SEO from '../components/SEO/SEO';

// Misc imports
import api from '../api';
import { NAV_BLOG } from '../constants';
import NewsletterSignup from '../components/Misc/NewsletterSignup';

// Constant declarations
const { Title, Paragraph } = Typography;

const BlogAuthorAvatar = ({ author }) => {
  if (author?.pictureUrl) {
    return <img src={author.pictureUrl} alt={author.name} className="blog-author-avatar" />;
  }
  const initials = author?.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
  return <div className="blog-author-avatar-placeholder">{initials}</div>;
};

const BlogTagBadge = ({ tag, onClick }) => (
  <span className="blog-tag-badge" onClick={onClick}>
    {tag.name}
  </span>
);

const TAKE = 8;

const BlogScreen = () => {
  const [posts, setPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [activeTag, setActiveTag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadPosts = useCallback(async (newSkip = 0, tagSlug = null, append = false) => {
    if (newSkip === 0) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const result = await api.fetchBlogPosts({ skip: newSkip, take: TAKE, tag: tagSlug });
      if (append) {
        setPosts(prev => [...prev, ...result.items]);
      } else {
        setPosts(result.items);
      }
      setTotal(result.total);
      setSkip(newSkip);
    } catch (_) {} finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    api.fetchBlogTags().then(setTags).catch(() => {});
    loadPosts(0, activeTag);
  }, []);

  const handleTagFilter = (tagSlug) => {
    const next = activeTag === tagSlug ? null : tagSlug;
    setActiveTag(next);
    loadPosts(0, next);
  };

  const handleLoadMore = () => {
    loadPosts(skip + TAKE, activeTag, true);
  };

  const hasMore = posts.length < total;

  return (
    <>
      <SEO
        title="Blog | Near-Death Experience Research & Insights"
        description="Articles, research, and insights on near-death experiences from the NDE Archive team. Explore topics like consciousness, afterlife studies, and NDE research."
        path="/blog"
        type="blog"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Blog',
            name: 'NDE Archive Blog',
            description: 'Articles and insights on near-death experiences',
            url: `${process.env.REACT_APP_SITE_URL || 'https://ndearchive.com'}/blog`
          }
        ]}
      />

      <div className="blog-page">
        <Row className="mb-32">
          <Col span={24}>
            <Title level={1} style={{ fontWeight: 800 }}>Blog</Title>
            <Paragraph className="text-color-black-80" style={{ fontSize: 18 }}>
              Explore articles, research, and insights on near-death experiences from the NDE Archive team. Our blog covers topics like consciousness, afterlife studies, and the latest NDE research findings.
            </Paragraph>
          </Col>
        </Row>

        {tags.length > 0 && (
          <div className="blog-tag-filters">
            <button
              className={`blog-tag-filter${!activeTag ? ' active' : ''}`}
              onClick={() => handleTagFilter(null)}
            >
              All
            </button>
            {tags.map(tag => (
              <button
                key={tag.id}
                className={`blog-tag-filter${activeTag === tag.slug ? ' active' : ''}`}
                onClick={() => handleTagFilter(tag.slug)}
              >
                {tag.name}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <Row gutter={[24, 24]}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Col key={i} xs={24} sm={12}>
                <Skeleton active paragraph={{ rows: 4 }} />
              </Col>
            ))}
          </Row>
        ) : posts.length === 0 ? (
          <Empty description="No posts yet. Check back soon!" />
        ) : (
          <>
            <Row gutter={[24, 24]}>
              {posts.map(post => (
                <Col key={post.id} xs={24} sm={12}>
                  <BlogCard post={post} />
                </Col>
              ))}
            </Row>

            {hasMore && (
              <div className="blog-load-more">
                <Button size="large" onClick={handleLoadMore} loading={loadingMore}>
                  Load more
                </Button>
              </div>
            )}
          </>
        )}

        <NewsletterSignup />
      </div>
    </>
  );
};

const BlogCard = ({ post }) => {
  const navigate = useNavigate();

  const publishedDate = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : null;

  return (
    <Link
      to={`/blog/${post.slug}`}
      style={{ display: 'block', color: 'inherit', textDecoration: 'none' }}
    >
      <Card
        className="blog-card"
        bordered={false}
        bodyStyle={{ display: 'flex', flexDirection: 'column', flex: 1 }}
        style={{ cursor: 'pointer' }}
        cover={
          post.coverImageUrl ? (
            <img src={post.coverImageUrl} alt={post.title} className="blog-card-cover" />
          ) : (
            <div className="blog-card-cover-placeholder">✍️</div>
          )
        }
      >
        {post.publishedAt && post.tags?.length > 0 && (
          <div className="blog-card-tags">
            {post.tags.map(tag => (
              <BlogTagBadge key={tag.id} tag={tag} onClick={(e) => {
                e.stopPropagation(); navigate(`${NAV_BLOG}?tag=${tag.slug}`);
              }} />
            ))}
          </div>
        )}

        <div className="blog-card-title">
          {post.title}
        </div>

        {post.excerpt && (
          <p className="blog-card-excerpt">{post.excerpt}</p>
        )}

        <div className="blog-card-footer">
          <BlogAuthorAvatar author={post.author} />
          <span className="blog-card-author-name">{post.author?.name}</span>
          {publishedDate && (
            <span className="blog-card-date">
              <CalendarOutlined className="mr-4" />
              {publishedDate}
            </span>
          )}
        </div>
      </Card>
    </Link>
  );
};

BlogAuthorAvatar.propTypes = {
  author: PropTypes.shape({
    name: PropTypes.string,
    pictureUrl: PropTypes.string
  })
};

BlogTagBadge.propTypes = {
  tag: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string.isRequired,
    slug: PropTypes.string
  }).isRequired,
  onClick: PropTypes.func
};

const postShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  excerpt: PropTypes.string,
  coverImageUrl: PropTypes.string,
  publishedAt: PropTypes.string,
  tags: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string, slug: PropTypes.string })),
  author: PropTypes.shape({ name: PropTypes.string, pictureUrl: PropTypes.string, slug: PropTypes.string })
});

BlogCard.propTypes = { post: postShape.isRequired };

export default BlogScreen;
